import { useCallback, useEffect, useState } from 'react';
import { webConfig } from '../../utils/webConfig';

const types = ['address'];
const componentRestrictions = { country: ['us', 'ca'] };
const googleAutocompleteUrl = `https://maps.googleapis.com/maps/api/js?key=${webConfig.googleAutocompleteApiKey}&libraries=places&callback=initMap&v=weekly`;

declare global {
    interface Window {
        initMap: () => void;
    }
}
const checkGoogle = () => {
    return Boolean(
        window.google?.maps?.places?.AutocompleteService &&
        window.google.maps.Geocoder,
    );
};

export const useGoogleAutocomplete = () => {
    const [autocomplete, setAutocomplete] = useState<
        google.maps.places.AutocompleteService | undefined
    >(undefined);
    const [geocoder, setGeocoder] = useState<google.maps.Geocoder | undefined>(
        undefined,
    );

    const handleMapInit = useCallback(() => {
        if (checkGoogle()) {
            setGeocoder(new window.google.maps.Geocoder());
            setAutocomplete(
                new window.google.maps.places.AutocompleteService(),
            );
        }
    }, [setAutocomplete, setGeocoder]);

    useEffect(() => {
        if (checkGoogle()) {
            return;
        }
        window.initMap = () => handleMapInit();
        const script = document.createElement('script');
        script.src = googleAutocompleteUrl;
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, [setAutocomplete, handleMapInit]);

    const makeGeocoderRequest = useCallback(
        (
            placeId: string,
            dumbGoogleCallback: (place: google.maps.GeocoderResult) => unknown,
        ) => {
            if (!geocoder) {
                handleMapInit();
                return;
            }
            geocoder.geocode({ placeId }).then((results) => {
                dumbGoogleCallback(results.results[0]);
            });
        },
        [geocoder, handleMapInit],
    );

    const makeLookupRequest = useCallback(
        (
            input: string,
            onPredictions: (
                predictions: google.maps.places.AutocompletePrediction[],
            ) => void,
        ) => {
            if (!autocomplete) {
                handleMapInit();
                return;
            }
            autocomplete.getPlacePredictions(
                {
                    input,
                    types,
                    componentRestrictions,
                },
                (predictions, status) => {
                    if (
                        predictions &&
                        status === google.maps.places.PlacesServiceStatus.OK
                    ) {
                        onPredictions(predictions);
                    }
                },
            );
        },
        [autocomplete, handleMapInit],
    );

    return { makeLookupRequest, makeGeocoderRequest };
};
